// src/ui/screens/RoundSetup.tsx — P4 pre-round-1 team override (D-37).
// Mounts only on round 1 pre-headstart. Diegetic rounds 2+ skip this entirely.
//
// T-04-23: Confirm button disabled when hunters or hiders team has 0 entries.
// T-04-24: touch-action: none on drag zone; overlay z-index above canvas;
//          onPointerDown preventDefault prevents canvas pointer event leakage.
// Plan 10-05 (WR-04): player's own chip is locked to the hider column —
//   non-draggable, marked data-locked="true", shows "(you)" label. This closes
//   the UI half of CR-01: the corrupted-roster path (player's characterId in
//   hunters) is unreachable via normal drag gestures.
//
// Source: .planning/phases/04-ai/04-CONTEXT.md D-37; 04-09-PLAN.md Task 2.

import { useEffect, useState, useCallback, type JSX } from 'react';
import { renderBus } from '../../render/bus';
import './round-setup.css';

type ColumnId = 'hunter' | 'hider' | 'bench';

interface RosterState {
  assignments: Record<string, ColumnId>;
}

export function RoundSetup(): JSX.Element | null {
  const [roundNumber, setRoundNumber] = useState<number | null>(null);
  const [roundPhase, setRoundPhase] = useState<string>('headstart');
  const [characters, setCharacters] = useState<string[]>([]);
  const [state, setState] = useState<RosterState | null>(null);
  const [dragging, setDragging] = useState<string | null>(null);
  // Plan 10-05 (WR-04): player's characterId received from the propose payload
  // so the player's own chip can be locked to the hider column.
  const [playerCharacterId, setPlayerCharacterId] = useState<string | null>(null);

  useEffect(() => {
    const offTick = renderBus.on('sim-tick', (e) => {
      setRoundNumber(e.round.roundNumber);
      setRoundPhase(e.round.phase);
    });
    // Receive initial roster from sim via 'round-setup-propose' event (Plan 10 wires this).
    const offRoster = renderBus.on('round-setup-propose', (e) => {
      const assignments: Record<string, ColumnId> = {};
      for (const id of e.hunters) assignments[id] = 'hunter';
      for (const id of e.hiders) assignments[id] = 'hider';
      for (const id of e.benched) assignments[id] = 'bench';
      // Plan 10-05 (WR-04): force the player's chip into hider on seed so the
      // lock is consistent from the first render (D-04: e0 always hider).
      const pid = e.playerCharacterId ?? null;
      if (pid) assignments[pid] = 'hider';
      setState({ assignments });
      setCharacters([...e.hunters, ...e.hiders, ...e.benched]);
      setPlayerCharacterId(pid);
    });
    return () => {
      offTick();
      offRoster();
    };
  }, []);

  const handleDrop = useCallback(
    (chipId: string, targetColumn: ColumnId) => {
      // Plan 10-05 (WR-04): ignore any drop that would move the player's own
      // chip out of the hider column. The chip is non-draggable (no setDragging
      // in RosterColumn for the locked chip), but guard here too for defence-
      // in-depth (external calls, future refactors, automated test helpers).
      if (chipId === playerCharacterId && targetColumn !== 'hider') {
        setDragging(null);
        return;
      }
      setState((prev) =>
        prev ? { assignments: { ...prev.assignments, [chipId]: targetColumn } } : prev,
      );
      setDragging(null);
    },
    [playerCharacterId],
  );

  const handleConfirm = useCallback(() => {
    if (!state) return;
    const hunters: string[] = [];
    const hiders: string[] = [];
    const benched: string[] = [];
    for (const [id, col] of Object.entries(state.assignments)) {
      if (col === 'hunter') hunters.push(id);
      else if (col === 'hider') hiders.push(id);
      else benched.push(id);
    }
    renderBus.emit('round-setup-confirmed', { hunters, hiders, benched });
    // P4 Plan 04-12 fix: dismiss the overlay. The mount gate depends on
    // `state !== null`; without this reset the overlay stays rendered while
    // the sim resumes underneath (pause is cleared by GameScene's confirmed
    // handler, but the UI doesn't know it should close).
    setState(null);
  }, [state]);

  // Mount gate (D-37): only show on round 1 pre-headstart, and only once a roster is proposed.
  if (roundNumber !== 1 || roundPhase !== 'headstart') return null;
  if (!state) return null;

  // D-07 (Plan 10-02): Confirm requires exactly 4 hunters AND exactly 4 hiders.
  // A lopsided split (5/3, 3/5, etc.) leaves Confirm disabled — the round cannot
  // start until a valid 4v4 is confirmed. Benny stays benched in v1 (sticky bench),
  // so 9 playable − 1 benched = 8 = 4v4 is always reachable.
  const hunterCount = characters.filter((c) => state.assignments[c] === 'hunter').length;
  const hiderCount = characters.filter((c) => state.assignments[c] === 'hider').length;
  const confirmDisabled = hunterCount !== 4 || hiderCount !== 4;

  return (
    <div
      className="round-setup-overlay"
      role="dialog"
      data-testid="round-setup"
    >
      <h2 style={{ color: '#eadfa2', textAlign: 'center', margin: '8px 0' }}>
        Choose your roster
      </h2>
      <div className="round-setup-columns">
        {(['hunter', 'hider', 'bench'] as const).map((col) => (
          <RosterColumn
            key={col}
            id={col}
            characters={characters}
            assignments={state.assignments}
            dragging={dragging}
            setDragging={setDragging}
            onDrop={handleDrop}
            lockedChipId={playerCharacterId}
          />
        ))}
      </div>
      <button
        className="round-setup-confirm"
        onClick={handleConfirm}
        disabled={confirmDisabled}
        data-testid="round-setup-confirm"
      >
        Confirm
      </button>
    </div>
  );
}

interface ColumnProps {
  id: ColumnId;
  characters: string[];
  assignments: Record<string, ColumnId>;
  dragging: string | null;
  setDragging: (id: string | null) => void;
  onDrop: (chipId: string, targetColumn: ColumnId) => void;
  // Plan 10-05 (WR-04): the player's own chip id — rendered as locked
  // (non-draggable, data-locked="true", shows a "(you)" label).
  lockedChipId: string | null;
}

function RosterColumn(props: ColumnProps): JSX.Element {
  const { id, characters, assignments, dragging, setDragging, onDrop, lockedChipId } = props;

  return (
    <div
      className="round-setup-column"
      data-column={id}
      onPointerUp={(e) => {
        const chipId = dragging;
        if (chipId) {
          e.preventDefault();
          onDrop(chipId, id);
        }
      }}
    >
      <h3>{id}</h3>
      {characters
        .filter((c) => assignments[c] === id)
        .map((c) => {
          const isLocked = c === lockedChipId;
          return (
            <div
              key={c}
              className="round-setup-chip"
              data-testid={`chip-${c}`}
              data-dragging={dragging === c ? 'true' : 'false'}
              data-locked={isLocked ? 'true' : undefined}
              onPointerDown={(e) => {
                // Plan 10-05 (WR-04): locked chip (player's own) is not
                // draggable — skip setDragging to prevent the drag gesture
                // from starting. The chip stays in the hider column always.
                if (isLocked) {
                  e.preventDefault();
                  return;
                }
                // T-04-24: prevent canvas from receiving pointer events during drag.
                e.preventDefault();
                // 04-12 fix: the browser takes implicit pointer capture on
                // pointerdown, which routes subsequent pointermove/pointerup
                // events to this chip instead of the element under the finger.
                // Release the capture so the destination column's onPointerUp
                // actually fires. Guarded with hasPointerCapture because
                // releasePointerCapture throws in some browsers if the pointer
                // is not currently captured.
                if (e.currentTarget.hasPointerCapture?.(e.pointerId)) {
                  e.currentTarget.releasePointerCapture(e.pointerId);
                }
                setDragging(c);
              }}
            >
              {c}{isLocked ? ' (you)' : ''}
            </div>
          );
        })}
    </div>
  );
}
